/**
 * <card-quick-add>
 * In-catalog variant picker + Add to Cart + Buy it now for product cards.
 * Reuses the theme's global `Cart` helper and <cart-drawer>. Add to Cart opens
 * the drawer; Buy it now adds the selected variant then goes straight to checkout.
 */
if (!customElements.get('card-quick-add')) {
  class CardQuickAdd extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-qa-variants]');
      try {
        this.variants = JSON.parse(json.textContent);
      } catch (e) {
        this.variants = [];
      }

      this.select = this.querySelector('[data-qa-variant-select]');
      this.button = this.querySelector('[data-qa-add]');
      this.buyNow = this.querySelector('[data-qa-buynow]');
      this.card = this.closest('.card-product');
      this.priceEl = this.card ? this.card.querySelector('.card-product__price') : null;

      if (!this.button) return;

      if (this.select) {
        this.select.addEventListener('change', this.onOptionChange.bind(this));
      }
      this.button.addEventListener('click', this.onAdd.bind(this));
      if (this.buyNow) this.buyNow.addEventListener('click', this.onBuyNow.bind(this));

      this.onOptionChange();
    }

    getCurrentVariant() {
      if (this.variants.length === 0) return null;
      // Single dropdown lists the real variants by id — pick straight from it.
      if (this.select) {
        const id = parseInt(this.select.value, 10);
        return this.variants.find((v) => v.id === id) || null;
      }
      // No dropdown = single-variant product.
      return this.variants[0];
    }

    onOptionChange() {
      const variant = this.getCurrentVariant();
      this.currentVariant = variant;
      const unavailable = !variant || !variant.available;

      if (!variant) {
        this.setButton(true, this.button.dataset.unavailableLabel || 'Unavailable');
      } else if (!variant.available) {
        this.setButton(true, this.button.dataset.soldoutLabel || 'Sold out');
      } else {
        this.setButton(false, this.button.dataset.label || 'Add to cart');
      }

      if (this.buyNow) {
        this.buyNow.disabled = unavailable;
        this.buyNow.classList.toggle('is-disabled', unavailable);
      }

      if (this.priceEl && variant && variant.price) {
        this.priceEl.textContent = variant.price;
      }
    }

    setButton(disabled, label) {
      this.button.disabled = disabled;
      this.button.classList.toggle('is-disabled', disabled);
      this.button.textContent = label;
    }

    buildFormData(variant, withSections) {
      const fd = new FormData();
      fd.append('id', variant.id);
      fd.append('quantity', 1);
      if (withSections) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          fd.append('sections', cartDrawer.getSectionsToRender().map((s) => s.section));
          fd.append('sections_url', window.location.pathname);
        }
      }
      return fd;
    }

    onAdd(event) {
      event.preventDefault();

      const variant = this.currentVariant;
      if (!variant || !variant.available) return;
      if (typeof Cart === 'undefined' || !Cart.add) return;

      const cartDrawer = document.querySelector('cart-drawer');
      const restoreLabel = this.button.textContent;
      this.button.classList.add('loading');
      this.button.disabled = true;

      Cart.add(this.buildFormData(variant, true))
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.button.textContent = restoreLabel;
            return;
          }
          if (cartDrawer && response.sections) {
            cartDrawer.renderContents(response);
          } else {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }
          this.flashAdded();
        })
        .catch((error) => console.error('[card-quick-add]', error))
        .finally(() => {
          this.button.classList.remove('loading');
          this.button.disabled = false;
        });
    }

    onBuyNow(event) {
      event.preventDefault();

      const variant = this.currentVariant;
      if (!variant || !variant.available) return;
      if (typeof Cart === 'undefined' || !Cart.add) return;

      this.buyNow.classList.add('loading');
      this.buyNow.disabled = true;

      Cart.add(this.buildFormData(variant, false))
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.buyNow.classList.remove('loading');
            this.buyNow.disabled = false;
            return;
          }
          window.location.href = '/checkout';
        })
        .catch((error) => {
          console.error('[card-quick-add]', error);
          this.buyNow.classList.remove('loading');
          this.buyNow.disabled = false;
        });
    }

    flashAdded() {
      const label = this.button.dataset.addedLabel || 'Added ✓';
      const original = this.button.dataset.label || 'Add to cart';
      this.button.textContent = label;
      window.setTimeout(() => {
        if (this.currentVariant && this.currentVariant.available) {
          this.button.textContent = original;
        }
      }, 1400);
    }
  }

  customElements.define('card-quick-add', CardQuickAdd);
}
